import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Select } from '@enamelai/design-system'
import type { AppointmentDetail } from '@pages/FormBuilder/assignedForms.types'
import './SelectAppointmentForKioskModal.css'

interface SelectAppointmentForKioskModalProps {
  show: boolean
  onHide: () => void
  patientName: string
  appointments: AppointmentDetail[]
  onSelect: (appointmentId: string) => void
}

const SelectAppointmentForKioskModal: React.FC<
  SelectAppointmentForKioskModalProps
> = ({ show, onHide, patientName, appointments, onSelect }) => {
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>('')

  useEffect(() => {
    if (!show) {
      setSelectedAppointmentId('')
    }
  }, [show])

  const appointmentOptions = useMemo(
    () =>
      appointments.map((appointment) => {
        const date = new Date(appointment.appointmentDate)
        const formattedDate = Number.isNaN(date.getTime())
          ? appointment.appointmentDate
          : `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`

        return {
          value: appointment.appointmentId || 'direct',
          label: appointment.appointmentId
            ? `${formattedDate} - ${appointment.appointmentType}`
            : 'Direct requests (no appointment)',
        }
      }),
    [appointments],
  )

  const handleClose = (): void => {
    setSelectedAppointmentId('')
    onHide()
  }

  const handleConfirm = (): void => {
    if (!selectedAppointmentId) return
    onSelect(selectedAppointmentId === 'direct' ? '' : selectedAppointmentId)
    setSelectedAppointmentId('')
  }

  return (
    <Modal
      open={show}
      onClose={handleClose}
      size="sm"
      title="Select forms for the kiosk"
      aria-label={`Select an appointment for the kiosk for ${patientName}`}
      secondaryLabel="Cancel"
      primaryLabel="Generate OTP"
      primaryDisabled={!selectedAppointmentId}
      onSecondaryClick={handleClose}
      onPrimaryClick={handleConfirm}
      className="select-appointment-for-kiosk-modal"
    >
      <div className="select-appointment-for-kiosk-field">
        <div className="select-appointment-for-kiosk-label">
          Select appointment schedules:{' '}
          <span className="select-appointment-for-kiosk-patient-name">
            {patientName}
          </span>
        </div>
        <Select
          label="Select appointment schedules"
          placeholder="Select"
          options={appointmentOptions}
          value={selectedAppointmentId}
          onChange={(value) => {
            if (typeof value === 'string') {
              setSelectedAppointmentId(value)
            }
          }}
          className="select-appointment-for-kiosk-select"
        />
      </div>
    </Modal>
  )
}

export default SelectAppointmentForKioskModal
